/**
 * Presentation for `AdminSite.lifecycle_state`.
 *
 * The classification itself is server-side (`admin.py::_lifecycle_state`)
 * so it is tested once and cannot disagree between this console and any
 * future CLI. What lives here is only how each state should READ, and
 * the one rule that matters is that a drift state must never be
 * rendered in a tone that says "fine".
 *
 * Every entry carries an `explanation` because the states are not
 * self-evident from a two-word chip: the difference between
 * `incomplete_binding` and `configured_not_deployed` is whether
 * running the operator checklist would help at all, and that is
 * exactly the question the reader has.
 */
import type { StatusKind } from '@shared/design-system/tokens';
import type { LifecycleState } from '../api/adminClient';

export interface LifecyclePresentation {
  label: string;
  status: StatusKind;
  explanation: string;
  /** Whether this state needs someone to DO something. Drives the
   *  "needs attention" filter and the ordering in the Sites table --
   *  at 300 sites, the rows worth looking at must not be found by
   *  scrolling. */
  needsAttention: boolean;
}

export const LIFECYCLE: Record<LifecycleState, LifecyclePresentation> = {
  live: {
    label: 'live',
    status: 'active',
    explanation: 'Commissioned, granted bus access, and its worker is publishing.',
    needsAttention: false,
  },
  degraded: {
    label: 'degraded',
    status: 'warning',
    explanation:
      "The worker's heartbeat has gone quiet for more than 10s. Usually the worker or the site's RMF stack, not the bus.",
    needsAttention: true,
  },
  configured_not_deployed: {
    label: 'no heartbeat',
    status: 'warning',
    explanation:
      'The record and binding are complete, but nothing is heartbeating. Either the operator checklist (cert, ACL, worker deploy) was never run, or the worker is down. This console cannot tell which — check the worker container first.',
    needsAttention: true,
  },
  incomplete_binding: {
    label: 'incomplete binding',
    status: 'idle',
    explanation:
      'No platform_bus_endpoint, so this site cannot be granted bus access at all. Running the operator checklist would not help; fix the binding first.',
    needsAttention: true,
  },
  retired: {
    label: 'retired',
    status: 'offline',
    explanation: 'Archived and silent. Decommissioning looks complete.',
    needsAttention: false,
  },
  retired_still_publishing: {
    label: 'retired — STILL PUBLISHING',
    status: 'error',
    explanation:
      'Archived, but its worker is still heartbeating. The bus ACL was never regenerated, so this site’s certificate STILL HAS WRITE ACCESS to the platform bus. Run the decommission checklist now.',
    needsAttention: true,
  },
};

/**
 * Sort order for the Sites table. `retired_still_publishing` is first
 * unconditionally: it is the only state that is a live security
 * problem rather than an availability one, and at any real site count
 * it would otherwise sit somewhere in the middle of an alphabetical
 * list, indistinguishable from a row nobody needs to read.
 */
const SEVERITY: Record<LifecycleState, number> = {
  retired_still_publishing: 0,
  configured_not_deployed: 1,
  degraded: 2,
  incomplete_binding: 3,
  live: 4,
  retired: 5,
};

export function lifecycleSeverity(state: LifecycleState): number {
  return SEVERITY[state] ?? 99;
}

/**
 * Never returns undefined.
 *
 * This SPA is a separate deployable from the API and can be older or
 * newer than it, so a `lifecycle_state` this build has never heard of
 * is a real possibility. Indexing `LIFECYCLE` directly would return
 * `undefined` and take down the whole table on the first property
 * access — an unknown state must degrade to "I don't know what this
 * is, look at it", not to a blank console.
 */
export function presentationFor(state: LifecycleState): LifecyclePresentation {
  return (
    LIFECYCLE[state] ?? {
      label: state,
      status: 'warning',
      explanation:
        'This console does not recognise that lifecycle state — it is likely older than the API. Treat as needing attention until it can be read properly.',
      needsAttention: true,
    }
  );
}
